// src/context/AuthContext.tsx
import { jwtDecode } from "jwt-decode";
import {
 createContext,
 useContext,
 useState,
 ReactNode,
 useEffect,
} from "react";
import authApi from "services/authService";
import { useNavigate } from "react-router-dom";
import axios from "axios";
interface Props {
 children?: ReactNode;
 // any props that come into the component
}
interface RegisterData {
 name: string;
 username: string;
 email: string;
 password: string;
 confirmPassword?: string;
 captchaValue: string;
 acceptMarketingEmails?: boolean;
}

export interface User {
 _id: string;
 name: string;
 username: string;
 email: string;
 role: string;
}
interface AuthContextType {
 user: User | null;
 userInfo: object | null;
 token: string | null;
 isAuthenticated: boolean;
 loading: boolean;
 login: (
  username: string,
  password: string,
  captchaValue: string
 ) => Promise<void>;
 register: (registerData: RegisterData) => Promise<void>;
 logout: (shouldNavigate: boolean) => void;
 refreshUserInfo: () => Promise<void>;
}
export const AuthContext = createContext<AuthContextType>(null as any);
export const AuthProvider = ({ children }: Props) => {
 const [user, setUser] = useState(null as any);
 const [userInfo, setUserInfo] = useState(null as any);
 const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
 const [loading, setLoading] = useState(true);
 // Simulate an authentication check
 const navigate = useNavigate();
 useEffect(() => {
  const storedToken = localStorage.getItem("token");
  setToken(storedToken);
  
  // Decode token FIRST (synchronously) to set user immediately
  if (storedToken) {
   try {
    const decoded = jwtDecode<User>(storedToken);
    setUser(decoded as any);
   } catch (decodeError) {
    // Invalid token, clear everything
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setUserInfo(null);
    setLoading(false);
    return;
   }
  } else {
   // No token, clear state and stop loading
   setUser(null);
   setUserInfo(null);
   setLoading(false);
   return;
  }

  // Fetch user profile from API (async, non-blocking)
  // This happens AFTER user is set from token, so pages can render immediately
  const setProfile = async () => {
   try {
    if (storedToken && !userInfo) {
     const response = await authApi.getUserProfile();
     setUserInfo(response);
    }
   } catch (profileError: any) {
    // If token is expired (401), clean up and logout
    if (profileError?.response?.status === 401) {
     localStorage.removeItem("token");
     setToken(null);
     setUser(null);
     setUserInfo(null);
     setLoading(false);
     navigate("/login");
     return;
    }
    // API call failed, but we still have user from token
    // Keep user but don't set userInfo (will retry on next mount if needed)
    console.error("Error fetching user profile:", profileError);
   } finally {
    setLoading(false);
   }
  };
  setProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 const login = async (
  usernameOrEmail: string,
  password: string,
  captchaValue: string
 ): Promise<void> => {
  const response = await authApi.login({ usernameOrEmail, password, captchaValue });
  const newToken = response.token;
  localStorage.setItem("token", newToken);
  setToken(newToken);
  setUser(jwtDecode<User>(newToken));
  setUserInfo(response);
  axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
 };

 const register = async (registerData: RegisterData): Promise<void> => {
  const response = await authApi.register(registerData);
  const newToken = response.token;
  localStorage.setItem("token", newToken);
  setToken(newToken);
  setUser(jwtDecode<User>(newToken));
  setUserInfo(response);
  axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
 };

 const logout = (shouldNavigate: boolean = true): void => {
  localStorage.removeItem("token");
  setToken(null);
  setUser(null);
  setUserInfo(null);
  if (shouldNavigate) navigate("/login");
 };

 const refreshUserInfo = async (): Promise<void> => {
  const storedToken = localStorage.getItem("token");
  if (!storedToken) {
   return;
  }
  try {
   const response = await authApi.getUserProfile();
   setUserInfo(response);
  } catch (error: any) {
   // If token is expired (401), clean up and logout
   if (error?.response?.status === 401) {
    logout();
    return;
   }
   console.error("Error refreshing user profile:", error);
  }
 };

 const isAuthenticated = !!user;

 return (
  <AuthContext.Provider
   value={{ user, userInfo, token, isAuthenticated, login, logout, register, loading, refreshUserInfo }}
  >
   {children}
  </AuthContext.Provider>
 );
};

export const useAuth = () => {
 return useContext(AuthContext);
};
